/**
 * Redux thunk action creators for the Projects feature.
 * Backed by the real Projects API. See `facades/projectsApi.ts`.
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import * as ProjectsApi from '../facades/projectsApi';
import { ApiErrorPayload, toApiErrorPayload } from '../facades/apiFacade';
import { Project } from '../../@types/projects';
import type { ThunkAPI } from '../stores/store';

type ProjectsThunkAPI = ThunkAPI & { rejectValue: ApiErrorPayload };

/**
 * The administrative list: every project the caller's org owns. Rejects with
 * `status: 403` for a non-admin, which the page renders as the read-only view
 * rather than as an error.
 */
export const fetchProjects = createAsyncThunk<Project[], { accessToken: string }, ProjectsThunkAPI>(
    'projects/fetchProjects',
    async ({ accessToken }, thunkApi) => {
        try {
            return await ProjectsApi.getProjects(accessToken);
        } catch (error) {
            return thunkApi.rejectWithValue(toApiErrorPayload(error));
        }
    }
);

/** The membership list: projects the caller is actually on, across orgs. */
export const fetchMyProjects = createAsyncThunk<Project[], { accessToken: string }, ProjectsThunkAPI>(
    'projects/fetchMyProjects',
    async ({ accessToken }, thunkApi) => {
        try {
            return await ProjectsApi.getMyProjects(accessToken);
        } catch (error) {
            return thunkApi.rejectWithValue(toApiErrorPayload(error));
        }
    }
);

export const fetchProject = createAsyncThunk<Project, { accessToken: string; projectId: string }, ProjectsThunkAPI>(
    'projects/fetchProject',
    async ({ accessToken, projectId }, thunkApi) => {
        try {
            return await ProjectsApi.getProject(accessToken, projectId);
        } catch (error) {
            return thunkApi.rejectWithValue(toApiErrorPayload(error));
        }
    }
);

export const createProject = createAsyncThunk<Project, { accessToken: string; name: string }, ProjectsThunkAPI>(
    'projects/createProject',
    async ({ accessToken, name }, thunkApi) => {
        try {
            return await ProjectsApi.createProject(accessToken, name);
        } catch (error) {
            return thunkApi.rejectWithValue(toApiErrorPayload(error));
        }
    }
);

export const renameProject = createAsyncThunk<
    Project,
    { accessToken: string; projectId: string; name: string },
    ProjectsThunkAPI
>('projects/renameProject', async ({ accessToken, projectId, name }, thunkApi) => {
    try {
        return await ProjectsApi.renameProject(accessToken, projectId, name);
    } catch (error) {
        return thunkApi.rejectWithValue(toApiErrorPayload(error));
    }
});

/** Resolves with the deleted project's object_id so the reducer can drop the row. */
export const deleteProject = createAsyncThunk<
    string,
    { accessToken: string; projectId: string },
    ProjectsThunkAPI
>('projects/deleteProject', async ({ accessToken, projectId }, thunkApi) => {
    try {
        await ProjectsApi.deleteProject(accessToken, projectId);
        return projectId;
    } catch (error) {
        return thunkApi.rejectWithValue(toApiErrorPayload(error));
    }
});
